/**
 * Dynamic Route Handler — handles both domain and slug cases
 */
import React, { useEffect } from 'react';
import Home from '@/containers/Home';
import Layout from '@/components/Layout';
import DomainExpired from '@/components/DomainExpired';
import AdditionalPage from '@/containers/AdditionalPage';

import { getWebpageTitle } from '@/utils/getWebpageTitle';
import { getMetaDescription } from '@/utils/getMetaDescription';
import { checkForWrongDomain } from '@/utils/checkForWrongDomain';
import { getRequiredCtxProps } from '@/utils/getRequiredCtxProps';
import { getSSRStoreData } from '@/utils/ssrPageData';
import { emptyCart } from '@/redux/actions';
import { useDispatch } from 'react-redux';
import checkIfThemePreview from '@/utils/checkIfThemePreview';
import { isWebsiteInternallyRouted } from '@/utils/isWebsiteInternallyRouted';
import { set404Response } from '@/utils/404Redirect';
import { useSetSSRStore, useSSRSelector } from 'src/redux';
import { useSelector as useReduxSelector } from '../redux';
import { getAdditionalPage } from 'src/services';
import { handleCategoryCollectionRedirect } from 'src/utils/productUtils';
import Error404 from 'src/containers/404Page';
import { fetchAdditionalPageSeoDetails } from 'src/utils/fetchAdditionalPageSeoDetails';


const DynamicPage = (props) => {
  const dispatch = useDispatch();
  useSetSSRStore(props.ssrStore);
  const storeInfo = useSSRSelector((state) => state.storeReducer.store);
  const reduxStoreData = useReduxSelector((state) => state.storeReducer.store);
  const isPreview = checkIfThemePreview();

  // Clear cart if store changes
  useEffect(() => {
    if (storeInfo?.store_id !== reduxStoreData?.store_id) {
      dispatch(emptyCart());
    }
  }, []);

  if (props?.notFound) {
    return <Error404 />
  }

  // Additional Page view
  if (props._addPage) {
    const advCustomSeo = props?.advCustomSeoDetails;
    return (
      <Layout
        store={storeInfo}
        title={advCustomSeo?.title?.value || `${props?._addPage?.page_name} - ${storeInfo?.store_info?.name}`}
        Icon={storeInfo?.store_info?.logo_image}
        metaDescription={
          advCustomSeo?.description ||
          'Now order online from my whatsapp store & pay using 150+ UPI Apps'
        }
        OGTitle={advCustomSeo?.og_title?.value || null}
        OGDescription={advCustomSeo?.og_description || null}
        OGImage={storeInfo?.store_info?.logo_image}
        isCustomDomain={props.isCustomDomain}
        canonicalTagUrl={props?._addPage?.redirect_url}
        {...props}
      >
        <AdditionalPage {...props} />
      </Layout>
    );
  }

  // Default Product/Home view
  return (
    <Layout
      store={storeInfo}
      title={storeInfo && getWebpageTitle(storeInfo, props.seo)}
      keywords={props.seo ? props?.seo?.keywords?.toString() : ''}
      Icon={storeInfo?.store_info?.logo_image}
      favIcon={storeInfo?.favicon}
      metaDescription={getMetaDescription(storeInfo, props.seo)}
      OGImage={storeInfo?.store_info?.logo_image}
      isCustomDomain={props.isCustomDomain}
      storeInfo={storeInfo}
      canonicalTagUrl={storeInfo?.store_url}
      {...props}
    >
      {!isPreview && storeInfo?.domain_info?.status === 2 ? (
        <DomainExpired storeInfo={storeInfo} />
      ) : (
        <Home {...props} storeInfo={storeInfo} />
      )}
    </Layout>
  );
};

export async function getServerSideProps(ctx) {
  const requesterDomain = ctx?.req?.headers?.host;
  const { domain, template_id, preview } = ctx.params;
  if (checkForWrongDomain(ctx)) return {};
  // in case of additional pages (without p) domain/slug, domain param will be slug, so we need to set it to actual domain
  // to fetch store details
  ctx.params.domain = requesterDomain;
  ctx.query.domain = requesterDomain;

  // Base SSR data (needed for both cases)
  const ssrPropData = await getSSRStoreData(ctx, {
    additionalDataToFetch: [
      'theme',
      'collections',
      'categories',
      'themeHtml',
      'seo',
      'coupons',
      'advancedSeo',
      'additionalPagesList'
    ],
  });

  const storeId = ssrPropData?.ssrStore?.storeReducer?.store?.store_id;

  // Try to match as Additional Page slug
  const _addpageResponse = storeId && domain
    ? await getAdditionalPage(
      domain,
      storeId,
      Boolean(Number(preview)),
      template_id,
      1
    )
    : null;
  const propData = {advCustomSeoDetails : null};
  if (_addpageResponse?.data?.status && domain !== requesterDomain) {
    const _addPage = JSON.parse(JSON.stringify(_addpageResponse.data.page)) || null;

    // Fetch SEO details for Additional Page
    propData.advCustomSeoDetails = await fetchAdditionalPageSeoDetails({
      type: 'additional_page',
      typeId: _addPage?.page_id,
      storeInfo: ssrPropData.ssrStore.storeReducer.store,
    });
    if(_addpageResponse?.data?.page?.is_redirected) {
      const redirectionLink = _addpageResponse?.data?.page?.redirect_url;
      return handleCategoryCollectionRedirect({ ctx, redirectionLink });
    }

    return {
      props: {
        ...ssrPropData,
        _addPage,
        ...getRequiredCtxProps(ctx, ssrPropData),
        ...propData
      },
    };
  } else {
    // Handle /[domain] with internal routing checks
    if (!isWebsiteInternallyRouted(requesterDomain) && domain !== requesterDomain) {
      return set404Response(ctx, ssrPropData);
    }
  }
  // If not additional page, treat as normal domain
  return {
    props: {
      ...ssrPropData,
      ...getRequiredCtxProps(ctx, ssrPropData),
    },
  };
}

export default DynamicPage;
